
import type { Calibration, DrawingElement, GearType, RopeContact } from './types'
import { getOfflineSessionOwner } from '@/lib/offline/session-offline-data'

// Bump when the on-disk/localStorage shape changes in a non-backward-compatible
// way so we can migrate or reject old payloads.
export const DIAGRAM_SCHEMA_VERSION = 2

export const RIGGING_STORAGE_PREFIX = 'h3safe-rigging-'

const STORAGE_KEY = 'h3safe-rigging-diagram:v1'
const SCALE_KEY = 'h3safe-rigging-scale:v1'

function scopedStorageKey(baseKey: string, documentKey?: string): string {
  const owner = getOfflineSessionOwner()
  if (owner) {
    return `${baseKey}:u${owner.userId}:o${owner.orgId ?? 'none'}${documentKey ? `:${documentKey}` : ''}`
  }
  return documentKey ? `${baseKey}:${documentKey}` : baseKey
}

export function clearRiggingDiagramStorage(): void {
  if (typeof window === 'undefined') return
  try {
    const keys = Object.keys(window.localStorage)
    for (const key of keys) {
      if (key.startsWith(RIGGING_STORAGE_PREFIX)) {
        window.localStorage.removeItem(key)
      }
    }
  } catch {
    // Best-effort cleanup.
  }
}

export function countRiggingDiagramDrafts(): number {
  if (typeof window === 'undefined') return 0
  return Object.keys(window.localStorage).filter((key) => key.startsWith(STORAGE_KEY)).length
}

export type DiagramFile = {
  schemaVersion: number
  savedAt: string
  elements: DrawingElement[]
  calibration?: Calibration | null
  baseSignature?: string
  baseRevision?: number | null
  // Monotonic counter for the next legend number to assign to a new gear.
  nextLegendNumber?: number
}

export type DiagramSaveSnapshot = {
  diagram: DiagramFile
  pngDataUrl: string | null
}

function createElementId(kind: DrawingElement['kind']): string {
  return `${kind}-${crypto.randomUUID()}`
}

export function cloneDiagramWithFreshElementIds(
  diagram: DiagramFile,
  createId: (kind: DrawingElement['kind']) => string = createElementId,
): DiagramFile {
  const idMap = new Map(
    diagram.elements.map((element) => [element.id, createId(element.kind)]),
  )
  const remapId = (id: string) => idMap.get(id) ?? id

  return {
    ...diagram,
    savedAt: new Date().toISOString(),
    elements: diagram.elements.map((element): DrawingElement => {
      if (element.kind === 'gear') {
        return {
          ...element,
          id: remapId(element.id),
          parentElementId: element.parentElementId
            ? remapId(element.parentElementId)
            : undefined,
        }
      }
      if (element.kind === 'rope') {
        return {
          ...element,
          id: remapId(element.id),
          start: element.start.mode === 'attached'
            ? { ...element.start, elementId: remapId(element.start.elementId) }
            : element.start,
          end: element.end.mode === 'attached'
            ? { ...element.end, elementId: remapId(element.end.elementId) }
            : element.end,
          contacts: element.contacts.map((contact) => ({
            ...contact,
            elementId: remapId(contact.elementId),
          })),
        }
      }
      return { ...element, id: remapId(element.id) }
    }),
  }
}

export function createDiagramSaveSnapshot(
  diagram: DiagramFile,
  capturePng?: (() => string | null) | null,
): DiagramSaveSnapshot {
  return {
    diagram,
    pngDataUrl: capturePng?.() ?? null,
  }
}

export function resolveDiagramSaveTarget(
  scheduledTargetId: number | null,
  scheduledSessionKey: string | number,
  currentTargetId: number | null,
  currentSessionKey: string | number,
  currentSessionActive = true,
): number | null | undefined {
  if (scheduledTargetId !== null) return scheduledTargetId
  if (!currentSessionActive) return undefined
  if (scheduledSessionKey !== currentSessionKey) return undefined
  return currentTargetId
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize)
  if (!value || typeof value !== 'object') return value
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, canonicalize(entry)]),
  )
}

export function diagramSignature(
  elements: DrawingElement[],
  nextLegendNumber: number,
): string {
  let legendNumber = elements.reduce(
    (maximum, element) => (
      element.kind === 'gear' && typeof element.legendNumber === 'number'
        ? Math.max(maximum, element.legendNumber)
        : maximum
    ),
    0,
  )
  const normalizedElements = elements.map((element) => {
    if (element.kind !== 'gear' || typeof element.legendNumber === 'number') return element
    legendNumber += 1
    return { ...element, legendNumber }
  })
  return JSON.stringify(canonicalize({ elements: normalizedElements, nextLegendNumber }))
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

const VALID_GEAR_TYPES = new Set<GearType>([
  'anchor', 'pulley', 'deviation', 'carabiner', 'rope-grab', 'descender',
  're-anchor', 'technician', 'casualty', 'load', 'i-beam-h', 'i-beam-v',
  'stretcher', 'edge', 'edge-left', 'edge-right',
])

const VALID_SHAPE_TYPES = new Set(['line', 'arrow', 'text', 'rect', 'ellipse', 'i-beam'])

const VALID_CONTACT_TYPES = new Set<RopeContact['contactType']>([
  'pulley', 'deviation', 'connector',
])

function isRopeContact(value: unknown): value is RopeContact {
  if (!value || typeof value !== 'object') return false
  const c = value as Record<string, unknown>
  return (
    typeof c.elementId === 'string' &&
    typeof c.snapPointId === 'string' &&
    VALID_CONTACT_TYPES.has(c.contactType as RopeContact['contactType'])
  )
}

function isRopeEnd(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false
  const end = value as Record<string, unknown>
  if (end.mode === 'free') {
    return (
      isFiniteNumber(end.x) &&
      isFiniteNumber(end.y) &&
      typeof end.fallsToGround === 'boolean'
    )
  }
  if (end.mode === 'attached') {
    return typeof end.elementId === 'string' && typeof end.snapPointId === 'string'
  }
  return false
}

// Validate that a parsed value is a structurally complete DrawingElement.
// Stricter than a shallow id/kind check so user-imported JSON with valid-looking
// but incomplete elements can't slip through and produce NaN during rendering.
function isElementLike(value: unknown): value is DrawingElement {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  if (typeof record.id !== 'string') return false
  switch (record.kind) {
    case 'gear':
      return (
        VALID_GEAR_TYPES.has(record.gearType as GearType) &&
        isFiniteNumber(record.x) &&
        isFiniteNumber(record.y) &&
        isFiniteNumber(record.width) &&
        isFiniteNumber(record.height) &&
        isFiniteNumber(record.rotation)
      )
    case 'shape':
      return (
        VALID_SHAPE_TYPES.has(record.shapeType as string) &&
        isFiniteNumber(record.x) &&
        isFiniteNumber(record.y) &&
        isFiniteNumber(record.width) &&
        isFiniteNumber(record.height) &&
        isFiniteNumber(record.rotation) &&
        typeof record.stroke === 'string'
      )
    case 'rope':
      return (
        isRopeEnd(record.start) &&
        isRopeEnd(record.end) &&
        Array.isArray(record.contacts) &&
        (record.contacts as unknown[]).every(isRopeContact) &&
        typeof record.color === 'string' &&
        isFiniteNumber(record.diameter)
      )
    case 'freedraw':
      return (
        Array.isArray(record.points) &&
        (record.points as unknown[]).every((v) => typeof v === 'number' && Number.isFinite(v)) &&
        typeof record.stroke === 'string' &&
        isFiniteNumber(record.strokeWidth)
      )
    default:
      return false
  }
}

function isCalibration(value: unknown): value is Calibration {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return typeof record.pixelsPerMeter === 'number' && record.pixelsPerMeter > 0 && Number.isFinite(record.pixelsPerMeter)
}

export function serializeDiagram(
  elements: DrawingElement[],
  calibration?: Calibration | null,
  nextLegendNumber?: number,
  baseSignature?: string,
  baseRevision?: number | null,
): string {
  const file: DiagramFile = {
    schemaVersion: DIAGRAM_SCHEMA_VERSION,
    savedAt: new Date().toISOString(),
    elements,
    calibration: calibration ?? null,
    nextLegendNumber: nextLegendNumber ?? undefined,
    baseSignature,
    baseRevision,
  }
  return JSON.stringify(file, null, 2)
}

/**
 * Parse a diagram payload. Accepts either a wrapped `DiagramFile` or a bare
 * array of elements. Returns null if the JSON is invalid or the elements don't
 * look like drawing elements.
 */
export function parseDiagram(json: string): DrawingElement[] | null {
  const result = parseDiagramFile(json)
  if (!result) return null
  if (result.skipped > 0) {
    console.warn(`[parseDiagram] skipped ${result.skipped} malformed element(s)`)
  }
  return result.elements
}

/**
 * Parse a diagram payload into its elements plus any calibration. Accepts a
 * wrapped `DiagramFile` or a bare array of elements.
 */
export function parseDiagramFile(
  json: string,
): {
  elements: DrawingElement[]
  calibration: Calibration | null
  nextLegendNumber: number
  savedAt: string | null
  baseSignature: string | null
  baseRevision: number | null
  skipped: number
} | null {
  let data: unknown
  try {
    data = JSON.parse(json)
  } catch {
    return null
  }
  const rawElements = Array.isArray(data)
    ? data
    : (data as { elements?: unknown })?.elements
  if (!Array.isArray(rawElements)) return null

  // Migrate legacy rope role: 'belay' was renamed to 'backup'.
  // Migrate legacy working-rope colour: #000000 → #dc2626 (red) so existing
  // diagrams pick up the corrected IRATA default and role-switching works.
  for (const el of rawElements) {
    if (el && typeof el === 'object' && (el as Record<string, unknown>).kind === 'rope') {
      const rope = el as Record<string, unknown>
      if (rope.role === 'belay') rope.role = 'backup'
      if (rope.role === 'working' && rope.color === '#000000') rope.color = '#dc2626'
      if (rope.role === 'haul' && rope.color === '#2563eb') rope.color = '#ea580c'
      if (rope.role === 'backup' && rope.color === '#dc2626') rope.color = '#7c3aed'
    }
  }

  const valid = rawElements.filter(isElementLike) as DrawingElement[]
  const skipped = rawElements.length - valid.length

  // If the file contained elements but none survived validation, treat as
  // invalid so the caller can reject rather than silently loading an empty
  // canvas.
  if (valid.length === 0 && rawElements.length > 0) return null

  const rawCalibration = Array.isArray(data) ? null : (data as { calibration?: unknown }).calibration
  const rawSavedAt = Array.isArray(data) ? null : (data as { savedAt?: unknown }).savedAt
  const rawBaseSignature = Array.isArray(data) ? null : (data as { baseSignature?: unknown }).baseSignature
  const rawBaseRevision = Array.isArray(data) ? null : (data as { baseRevision?: unknown }).baseRevision

  // Migrate legend numbers: assign 1..N to gear elements that don't already
  // have a legendNumber (backward-compat for v1 diagrams).
  let maxLegend = 0
  for (const el of valid) {
    if (el.kind === 'gear' && typeof el.legendNumber === 'number') {
      maxLegend = Math.max(maxLegend, el.legendNumber)
    }
  }
  let counter = maxLegend
  for (const el of valid) {
    if (el.kind === 'gear' && typeof el.legendNumber !== 'number') {
      counter += 1
      ;(el as { legendNumber: number }).legendNumber = counter
    }
  }

  const rawNext = Array.isArray(data) ? undefined : (data as { nextLegendNumber?: unknown }).nextLegendNumber
  const nextLegendNumber = typeof rawNext === 'number' && rawNext > counter ? rawNext : counter + 1

  return {
    elements: valid,
    calibration: isCalibration(rawCalibration) ? rawCalibration : null,
    nextLegendNumber,
    savedAt: typeof rawSavedAt === 'string' ? rawSavedAt : null,
    baseSignature: typeof rawBaseSignature === 'string' ? rawBaseSignature : null,
    baseRevision: typeof rawBaseRevision === 'number' ? rawBaseRevision : null,
    skipped,
  }
}

export function saveCalibrationToStorage(calibration: Calibration | null, documentKey?: string): void {
  if (typeof window === 'undefined') return
  try {
    const key = scopedStorageKey(SCALE_KEY, documentKey)
    if (calibration) window.localStorage.setItem(key, JSON.stringify(calibration))
    else window.localStorage.removeItem(key)
  } catch {
    // best-effort
  }
}

export function loadCalibrationFromStorage(documentKey?: string): Calibration | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(scopedStorageKey(SCALE_KEY, documentKey))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return isCalibration(parsed) ? parsed : null
  } catch {
    return null
  }
}

export function saveDiagramToStorage(
  elements: DrawingElement[],
  nextLegendNumber?: number,
  documentKey?: string,
  baseSignature?: string,
  baseRevision?: number | null,
): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(
      scopedStorageKey(STORAGE_KEY, documentKey),
      serializeDiagram(
        elements,
        undefined,
        nextLegendNumber,
        baseSignature,
        baseRevision,
      ),
    )
  } catch {
    // Storage may be unavailable or over quota; autosave is best-effort.
  }
}

export function loadDiagramFileFromStorage(
  documentKey?: string,
): {
  elements: DrawingElement[]
  calibration: Calibration | null
  nextLegendNumber: number
  savedAt: string | null
  baseSignature: string | null
  baseRevision: number | null
  skipped: number
} | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(scopedStorageKey(STORAGE_KEY, documentKey))
    if (!raw) return null
    return parseDiagramFile(raw)
  } catch {
    return null
  }
}

export function canRecoverDiagramDraft(
  draft: ReturnType<typeof loadDiagramFileFromStorage>,
  backendElements: DrawingElement[],
  backendNextLegendNumber: number,
  backendRevision: number | null,
): boolean {
  return (
    draft !== null
    && backendRevision !== null
    && draft.baseRevision === backendRevision
    && draft.baseSignature === diagramSignature(backendElements, backendNextLegendNumber)
  )
}

export function reconcileDiagramStorage(
  documentKey: string,
  savedElements: DrawingElement[],
  savedNextLegendNumber: number,
  savedRevision: number | null,
  targetDocumentKey = documentKey,
): void {
  if (typeof window === 'undefined') return
  try {
    const stored = loadDiagramFileFromStorage(documentKey)
    const savedSignature = diagramSignature(savedElements, savedNextLegendNumber)
    const targetStorageKey = scopedStorageKey(STORAGE_KEY, targetDocumentKey)
    const targetScaleKey = scopedStorageKey(SCALE_KEY, targetDocumentKey)
    const sourceStorageKey = scopedStorageKey(STORAGE_KEY, documentKey)
    const sourceScaleKey = scopedStorageKey(SCALE_KEY, documentKey)

    if (!stored || diagramSignature(stored.elements, stored.nextLegendNumber) === savedSignature) {
      window.localStorage.removeItem(sourceStorageKey)
      window.localStorage.removeItem(sourceScaleKey)
      if (targetDocumentKey !== documentKey) {
        window.localStorage.removeItem(targetStorageKey)
        window.localStorage.removeItem(targetScaleKey)
      }
      return
    }

    window.localStorage.setItem(
      targetStorageKey,
      serializeDiagram(
        stored.elements,
        stored.calibration,
        stored.nextLegendNumber,
        savedSignature,
        savedRevision,
      ),
    )
    const scale = window.localStorage.getItem(sourceScaleKey)
    if (scale) window.localStorage.setItem(targetScaleKey, scale)
    if (targetDocumentKey !== documentKey) {
      window.localStorage.removeItem(sourceStorageKey)
      window.localStorage.removeItem(sourceScaleKey)
    }
  } catch {
    // Storage may be unavailable or over quota; autosave is best-effort.
  }
}

export function clearDiagramStorage(documentKey?: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(scopedStorageKey(STORAGE_KEY, documentKey))
    window.localStorage.removeItem(scopedStorageKey(SCALE_KEY, documentKey))
  } catch {
    // ignore
  }
}

export function downloadFile(filename: string, contents: string | Blob, mimeType = 'application/json'): void {
  if (typeof document === 'undefined') return
  const blob = typeof contents === 'string' ? new Blob([contents], { type: mimeType }) : contents
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export function timestampedName(prefix: string, extension: string): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
  return `${prefix}-${stamp}.${extension}`
}
